import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Compass, Users, MapPin, Mountain } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import Seo from "@/components/Seo";
import ExpeditionCard from "@/components/ExpeditionCard";

const HERO = "https://images.unsplash.com/photo-1558537112-ed307f81124c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const WHY_IMG = [
  "https://images.unsplash.com/photo-1659346031868-86f45634e4ee?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1608570201133-f678b991d02a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1639402479478-f5e7881c0ccc?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1519309621146-2a47d1f7103a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
];
const CTA_BG = "https://images.unsplash.com/photo-1764869870815-2abe1ab53f51?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

function CompassSvg() {
  return (
    <svg width="76" height="76" viewBox="0 0 100 100" fill="none" aria-hidden="true">
      <circle cx="50" cy="50" r="46" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      <circle cx="50" cy="50" r="37" stroke="currentColor" strokeWidth="0.75" opacity="0.3" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
        <line key={a} x1="50" y1="6" x2="50" y2="13" stroke="currentColor" strokeWidth="1.2" transform={`rotate(${a} 50 50)`} opacity="0.6" />
      ))}
      <polygon points="50,15 56,50 50,60 44,50" fill="#BC5D2E" />
      <polygon points="50,85 56,50 50,40 44,50" fill="currentColor" opacity="0.4" />
      <circle cx="50" cy="50" r="3.5" fill="#0A0A0A" stroke="currentColor" />
    </svg>
  );
}

function useInView(threshold = 0.18) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); o.disconnect(); } }, { threshold });
    o.observe(el);
    return () => o.disconnect();
  }, [threshold]);
  return [ref, seen];
}

function WhyChoose() {
  const { t } = useI18n();
  const [ref, seen] = useInView();
  const compassRef = useRef(null);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (compassRef.current) compassRef.current.style.transform = `rotate(${window.scrollY * 0.12}deg)`;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);
  const steps = [
    { img: WHY_IMG[0], icon: MapPin, key: t("why_1_t"), d: t("why_1_d") },
    { img: WHY_IMG[1], icon: Compass, key: t("why_2_t"), d: t("why_2_d") },
    { img: WHY_IMG[2], icon: Mountain, key: t("why_3_t"), d: t("why_3_d") },
    { img: WHY_IMG[3], icon: Users, key: t("why_4_t"), d: t("why_4_d") },
  ];
  return (
    <section ref={ref} className="py-24 sm:py-32 relative overflow-hidden" data-testid="why-choose-block">
      <div className="pointer-events-none absolute -top-32 right-0 w-[40rem] h-[40rem] rounded-full opacity-[0.12]" style={{ background: "radial-gradient(circle, #BC5D2E, transparent 65%)" }} />
      <div className="mx-auto max-w-7xl px-6 sm:px-12 relative">
        <div className={`flex items-start justify-between gap-6 ${seen ? "fade-up" : "opacity-0"}`}>
          <div>
            <span className="font-mono-hs text-xs text-[#BC5D2E] tracking-[0.3em] uppercase">{t("why_sub")}</span>
            <h2 className="font-clash font-bold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mt-4 max-w-2xl">{t("why_title")}</h2>
          </div>
          <div className="hidden sm:block shrink-0 pt-3 text-[#BC5D2E]/70" aria-hidden="true">
            <div ref={compassRef} className="will-change-transform"><CompassSvg /></div>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-14 lg:gap-y-0 relative">
          <div className="hidden lg:block absolute top-[56px] left-[12.5%] right-[12.5%] h-px border-t border-dashed border-white/15" />
          {steps.map((s, i) => (
            <div
              key={i}
              data-testid={`why-card-${i}`}
              className={`relative flex flex-col items-center text-center px-4 ${seen ? "fade-up" : "opacity-0"}`}
              style={seen ? { animationDelay: `${0.12 * (i + 1)}s` } : undefined}
            >
              <div className="relative">
                <div className="w-28 h-28 rounded-full overflow-hidden border border-white/10 ring-1 ring-[#BC5D2E]/30 bg-[#141414]">
                  <img src={s.img} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
                <span className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#0A0A0A] border border-[#BC5D2E]/50 flex items-center justify-center text-[#BC5D2E]"><s.icon size={16} /></span>
                <span className="absolute -top-1 -left-2 font-mono-hs text-[10px] tracking-widest text-[#BC5D2E]/60">0{i + 1}</span>
              </div>
              <h3 className="mt-7 font-clash text-lg tracking-tight leading-snug">
                <span className="bg-[#BC5D2E]/15 text-[#e0a583] px-1.5 py-0.5 box-decoration-clone">{s.key}</span>
              </h3>
              <p className="text-[#A3A3A3] text-sm mt-3 leading-relaxed max-w-[15rem]">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t, L, lang } = useI18n();
  const [exps, setExps] = useState([]);
  const [pc, setPc] = useState({});
  useEffect(() => { api.get("/expeditions").then(({ data }) => setExps(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/pages/home").then(({ data }) => setPc(data.content || {})).catch(() => {}); }, []);
  const featured = exps.slice(0, 3);

  return (
    <div>
      <Seo lang={lang} path="/" title={lang === "en" ? "Hors Signal - Small-group adventure travel" : "Hors Signal - Voyages d'aventure en petit groupe"} description={lang === "en" ? "Extreme, rare and confidential expeditions in very small groups." : "Expéditions extrêmes, rares et confidentielles en très petits groupes."} />
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] flex items-end overflow-hidden">
        <img src={HERO} alt="hero" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/50 to-black/40" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-12 pb-20 sm:pb-28 w-full">
          <span className="font-mono-hs text-xs sm:text-sm text-[#BC5D2E] tracking-[0.3em] uppercase fade-up">{pc.hero_kicker || t("hero_kicker")}</span>
          <h1 className="font-clash font-bold text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.9] mt-6 whitespace-pre-line fade-up" style={{ animationDelay: "0.1s" }}>
            {pc.hero_title || t("hero_title")}
          </h1>
          <p className="text-[#D4D4D4] text-base sm:text-lg max-w-xl mt-6 fade-up" style={{ animationDelay: "0.2s" }}>{pc.hero_sub || t("hero_sub")}</p>
          <div className="flex flex-wrap gap-4 mt-10 fade-up" style={{ animationDelay: "0.3s" }}>
            <Link to="/expeditions" data-testid="hero-cta" className="group bg-[#BC5D2E] text-white px-8 py-4 uppercase tracking-widest text-sm hover:bg-[#8A281E] transition-colors flex items-center gap-3">
              {pc.cta || t("hero_cta")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/histoire" data-testid="hero-cta-2" className="border border-white/30 text-white px-8 py-4 uppercase tracking-widest text-sm hover:border-white transition-colors">
              {t("nav_story")}
            </Link>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 py-4 overflow-hidden bg-[#0F0F0F]">
        <div className="marquee-track flex gap-12 font-mono-hs text-sm text-[#737373] uppercase tracking-widest">
          {[...Array(2)].map((_, k) => (
            <span key={k} className="flex gap-12">
              {exps.concat(exps).map((e, i) => <span key={i}>{L(e.location)} <span className="text-[#BC5D2E]">◆</span></span>)}
            </span>
          ))}
        </div>
      </div>

      {/* WHY CHOOSE HORS SIGNAL */}
      <WhyChoose />

      {/* CREDIBILITY STATS */}
      <section className="border-y border-white/10 bg-[#0F0F0F]">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 py-16 grid grid-cols-1 sm:grid-cols-3 gap-10 text-center sm:text-left" data-testid="stats-block">
          <div>
            <div className="font-clash text-5xl sm:text-6xl text-[#BC5D2E]">{exps.length || 9}</div>
            <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-2">{t("stats_exp")}</div>
          </div>
          <div>
            <div className="font-clash text-5xl sm:text-6xl text-[#BC5D2E]">12</div>
            <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-2">{t("stats_members")}</div>
          </div>
          <div>
            <div className="font-clash text-5xl sm:text-6xl text-[#BC5D2E]">2019</div>
            <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-2">{t("stats_since")}</div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-8 sm:py-16">
        <div className="mx-auto max-w-7xl px-6 sm:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-clash text-3xl sm:text-5xl uppercase tracking-tight">{t("section_selected")}</h2>
            </div>
            <Link to="/expeditions" className="hidden sm:flex items-center gap-2 text-sm uppercase tracking-widest text-[#BC5D2E] link-underline">
              {t("view_all")} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featured.map((e, i) => <ExpeditionCard key={e.slug} exp={e} index={i} />)}
          </div>
        </div>
      </section>

      {/* REASSURANCE / SECURITY */}
      <section className="py-24 sm:py-32 border-t border-white/10" data-testid="reassurance-block">
        <div className="mx-auto max-w-7xl px-6 sm:px-12">
          <h2 className="font-clash text-3xl sm:text-5xl uppercase tracking-tight">{t("reassurance_title")}</h2>
          <p className="text-[#A3A3A3] mt-3 max-w-xl">{t("reassurance_sub")}</p>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: Shield, t: t("reassurance_1_t"), d: t("reassurance_1_d") },
              { icon: Compass, t: t("reassurance_2_t"), d: t("reassurance_2_d") },
              { icon: Users, t: t("reassurance_3_t"), d: t("reassurance_3_d") },
            ].map((b, i) => (
              <div key={i} className="border border-white/10 bg-[#141414] p-8 hover:border-[#BC5D2E]/50 transition-colors">
                <b.icon size={28} className="text-[#BC5D2E]" />
                <h3 className="font-clash text-xl uppercase tracking-tight mt-5">{b.t}</h3>
                <p className="text-[#A3A3A3] text-sm mt-3 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS PLACEHOLDER (real reviews only) */}

      {/* FINAL CTA */}
      <section className="relative" data-testid="final-cta">
        <div className="relative h-[460px] sm:h-[580px] flex items-center justify-center text-center overflow-hidden">
          <img src={CTA_BG} alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
          {/* Fondu haut : morph doux depuis la section sombre au-dessus */}
          <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
          {/* Fondu bas + voile lisibilité : morph vers le footer */}
          <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
          <div className="relative px-6">
            <h2 className="font-clash font-bold text-4xl sm:text-6xl uppercase tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">{t("final_cta_title")}</h2>
            <Link to="/expeditions" data-testid="final-cta-btn" className="group inline-flex items-center gap-3 mt-8 bg-[#BC5D2E] text-white px-10 py-4 uppercase tracking-widest text-sm hover:bg-[#8A281E] transition-colors">
              {t("final_cta_btn")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
